import { Box, Button, Stack, Typography } from '@mui/material';
import { useUsersCalendarEventsList } from 'djangogen/client';
import { useListAllOwnedIoTAssets } from 'gen/client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useEnsureOwnerCalendars } from '../../Hooks/useEnsureOwnerCalendars';
import { useGetInstanceInfo } from '../../Hooks/useGetInstanceInfo';
import { roomEventColor } from '../../Utils/calendarColors';
import { dayjs } from '../Dayjs/dayjs-utils';

import InfoscreenMultiRoomCalendarView from './InfoscreenMultiRoomCalendarView';
import InfoscreenMultiRoomWeekCalendarView from './InfoscreenMultiRoomWeekCalendarView';
import {
    ALL_DAY_ROW_HEIGHT,
    clampMinutes,
    DAY_HEADER_HEIGHT,
    EVENT_LINE_HEIGHT,
    FALLBACK_GRID_BODY_HEIGHT_PX,
    formatCompactEventTimeRange,
    getConfiguredRoomGroupIds,
    getDayColumnData,
    getHourLinePercentage,
    getPrivateEventLabel,
    getTimedEventLayout,
    getUniformEventTypography,
    getVisibleTimeLabel,
    getWeekStart,
    HOURS_END,
    HOURS_START,
    isWeekendDay,
    TIME_AXIS_WIDTH,
    TOTAL_VISIBLE_MINUTES,
} from './roomCalendarUtils';

import type { MultiRoomTarget } from './InfoscreenMultiRoomCalendarView';
import type { Dashboard, RoomCalendarViewConfig } from '../../Types/AuthServer';
import type { PositionedEvent } from './roomCalendarUtils';

/** Box geometry of one event, derived in one place so the typography pass and the paint pass agree. */
function getEventHeightPercentage(event: PositionedEvent) {
    return Math.max(((event.endMinutes - event.startMinutes) * 100) / TOTAL_VISIBLE_MINUTES, 3.8);
}

function getEventWidthPx(event: PositionedEvent, dayColumnWidthPx: number) {
    return Math.max(Math.round(dayColumnWidthPx / Math.max(event.laneCount, 1) - 6), 42);
}

interface InfoscreenRoomCalendarViewProps {
    infoscreenLayout: Dashboard['socials'];
    roomCalendarConfig?: RoomCalendarViewConfig;
}

interface RoomWeekCalendarProps {
    calendarId: string;
    currentTime: dayjs.Dayjs;
    infoscreenLayout: Dashboard['socials'];
    roomTitle: string;
    showEventTitles: boolean;
    showWeekend: boolean;
    timezone: string;
}

interface InfoscreenMessageProps {
    infoscreenLayout: Dashboard['socials'];
    secondaryText?: string;
    text: string;
}

function InfoscreenMessage({ infoscreenLayout, secondaryText, text }: Readonly<InfoscreenMessageProps>) {
    return (
        <Stack
            sx={{
                alignItems: 'center',
                color: infoscreenLayout?.textColor || '#ffffff',
                height: '100%',
                justifyContent: 'center',
                px: 4,
                width: '100%',
            }}
        >
            <Typography sx={{ fontSize: 'clamp(18px, 2vw, 30px)', fontWeight: 600 }}>{text}</Typography>
            {secondaryText ? (
                <Typography sx={{ fontSize: 'clamp(14px, 1.2vw, 18px)', mt: 1.5, opacity: 0.8, textAlign: 'center' }}>
                    {secondaryText}
                </Typography>
            ) : null}
        </Stack>
    );
}

function RoomWeekCalendar({
    calendarId,
    currentTime,
    infoscreenLayout,
    roomTitle,
    showEventTitles,
    showWeekend,
    timezone,
}: Readonly<RoomWeekCalendarProps>) {
    const calendarGridRef = useRef<HTMLDivElement | null>(null);
    const [calendarGridWidthPx, setCalendarGridWidthPx] = useState(0);
    const [calendarGridHeightPx, setCalendarGridHeightPx] = useState(0);

    const weekStart = useMemo(() => getWeekStart(currentTime), [currentTime]);
    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => weekStart.add(index, 'day')), [weekStart]);
    const visibleDays = useMemo(
        () => (showWeekend ? weekDays : weekDays.filter((day) => !isWeekendDay(day))),
        [showWeekend, weekDays],
    );

    const { data: events, isLoading: isEventsLoading } = useUsersCalendarEventsList(
        {
            calendar: calendarId,
            end: weekStart.add(6, 'day').endOf('day').toISOString(),
            start: weekStart.toISOString(),
        },
        {
            swr: {
                enabled: Boolean(calendarId),
                refreshInterval: 60_000,
                revalidateOnFocus: false,
            },
        },
    );

    const weekRangeLabel = `${weekStart.format('D.M.YYYY')} - ${weekStart.add(6, 'day').format('D.M.YYYY')}`;
    const roomCalendarTitle = `${roomTitle} Calendar`;

    const dayData = useMemo(
        () =>
            visibleDays.map((day) =>
                getDayColumnData({
                    currentDay: day,
                    events: events ?? [],
                    showEventTitles,
                    timezone,
                }),
            ),
        [events, showEventTitles, timezone, visibleDays],
    );

    const hasTimedEvents = dayData.some((day) => day.positionedEvents.length > 0);
    const hasAllDayEvents = dayData.some((day) => day.allDayEvents.length > 0);
    const bodyRow = hasAllDayEvents ? 3 : 2;
    const visibleDayCount = Math.max(visibleDays.length, 1);
    const dayColumnWidthPx = Math.max((calendarGridWidthPx - TIME_AXIS_WIDTH) / visibleDayCount, 0);
    const measuredGridBodyHeightPx =
        calendarGridHeightPx - DAY_HEADER_HEIGHT - (hasAllDayEvents ? ALL_DAY_ROW_HEIGHT : 0);
    const gridBodyHeightPx = measuredGridBodyHeightPx > 0 ? measuredGridBodyHeightPx : FALLBACK_GRID_BODY_HEIGHT_PX;
    // One title size and one time size for the whole week, computed over every box at once, matching
    // the multi-room views. Sizing each box on its own made a short name in a short booking render
    // larger than a long name beside it, which reads as a rendering fault from across the lobby.
    const eventTypography = getUniformEventTypography(
        dayData.flatMap((columnData) =>
            columnData.positionedEvents.map((event) => ({
                eventHeightPx: (getEventHeightPercentage(event) / 100) * gridBodyHeightPx,
                eventWidthPx: getEventWidthPx(event, dayColumnWidthPx),
                label: event.publicLabel,
            })),
        ),
    );

    useEffect(() => {
        const gridElement = calendarGridRef.current;

        if (!gridElement) {
            return;
        }

        const measureGrid = () => {
            const nextGridWidthPx = Math.floor(gridElement.clientWidth);
            const nextGridHeightPx = Math.floor(gridElement.clientHeight);

            setCalendarGridWidthPx((currentWidth) => {
                if (currentWidth === nextGridWidthPx) {
                    return currentWidth;
                }

                return nextGridWidthPx;
            });
            setCalendarGridHeightPx((currentHeight) => {
                if (currentHeight === nextGridHeightPx) {
                    return currentHeight;
                }

                return nextGridHeightPx;
            });
        };

        measureGrid();

        let observer: ResizeObserver | null = null;

        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                measureGrid();
            });
            observer.observe(gridElement);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [calendarId, hasAllDayEvents, isEventsLoading]);

    if (isEventsLoading) {
        return <InfoscreenMessage infoscreenLayout={infoscreenLayout} text="Loading room calendar" />;
    }

    return (
        <Stack
            sx={{
                backgroundColor: infoscreenLayout?.backgroundColor || '#000000',
                boxSizing: 'border-box',
                color: infoscreenLayout?.textColor || '#ffffff',
                gap: 0.75,
                height: '100%',
                px: { sm: 1.5, xs: 1 },
                py: { sm: 1.25, xs: 1 },
                width: '100%',
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, justifyContent: 'space-between', pr: '104px' }}>
                <Box>
                    <Typography sx={{ fontSize: 'clamp(18px, 1.8vw, 28px)', fontWeight: 700 }}>
                        {roomCalendarTitle}
                    </Typography>
                    <Typography sx={{ fontSize: 'clamp(11px, 0.9vw, 15px)', opacity: 0.75 }}>
                        Current week · {weekRangeLabel}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 'clamp(13px, 1vw, 17px)', opacity: 0.9, pt: 0.35, textAlign: 'right' }}>
                    {currentTime.format('ddd D.M. HH:mm')}
                </Typography>
            </Stack>

            <Box
                ref={calendarGridRef}
                sx={{
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 1.5,
                    display: 'grid',
                    flex: 1,
                    gridTemplateColumns: `${TIME_AXIS_WIDTH.toString()}px repeat(${visibleDayCount.toString()}, minmax(0, 1fr))`,
                    gridTemplateRows: hasAllDayEvents
                        ? `${DAY_HEADER_HEIGHT.toString()}px ${ALL_DAY_ROW_HEIGHT.toString()}px minmax(0, 1fr)`
                        : `${DAY_HEADER_HEIGHT.toString()}px minmax(0, 1fr)`,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: '1 / 2',
                    }}
                />

                {visibleDays.map((day, index) => (
                    <Stack
                        key={`header-${day.format('YYYY-MM-DD')}`}
                        sx={{
                            backgroundColor: day.isSame(currentTime, 'day')
                                ? 'rgba(59,130,246,0.14)'
                                : 'rgba(255,255,255,0.03)',
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                            borderRight: index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                            gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                            gridRow: '1 / 2',
                            justifyContent: 'center',
                            px: 0.75,
                        }}
                    >
                        <Typography noWrap sx={{ fontSize: 'clamp(11px, 0.82vw, 13px)', fontWeight: 700 }}>
                            {`${day.format('ddd').toUpperCase()} ${day.format('D.M.')}`}
                        </Typography>
                    </Stack>
                ))}

                {hasAllDayEvents ? (
                    <>
                        <Box
                            sx={{
                                alignItems: 'center',
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderBottom: '1px solid rgba(255,255,255,0.12)',
                                borderRight: '1px solid rgba(255,255,255,0.12)',
                                display: 'flex',
                                gridColumn: '1 / 2',
                                gridRow: '2 / 3',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography sx={{ fontSize: '11px', letterSpacing: '0.08em', opacity: 0.7 }}>
                                ALL DAY
                            </Typography>
                        </Box>
                        {visibleDays.map((day, index) => (
                            <Stack
                                key={`all-day-${day.format('YYYY-MM-DD')}`}
                                sx={{
                                    backgroundColor: day.isSame(currentTime, 'day')
                                        ? 'rgba(59,130,246,0.16)'
                                        : 'rgba(255,255,255,0.03)',
                                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                                    borderRight:
                                        index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                    gap: 0.35,
                                    gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                                    gridRow: '2 / 3',
                                    px: 0.5,
                                    py: 0.4,
                                }}
                            >
                                {dayData[index]?.allDayEvents.length ? (
                                    dayData[index].allDayEvents.slice(0, 2).map((label) => (
                                        <Box
                                            key={`${day.format('YYYY-MM-DD')}-${label}`}
                                            sx={{
                                                backgroundColor: roomEventColor,
                                                borderRadius: 999,
                                                overflow: 'hidden',
                                                px: 0.8,
                                                py: 0.3,
                                            }}
                                        >
                                            <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600 }}>
                                                {showEventTitles ? label : getPrivateEventLabel(true)}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ fontSize: '11px', opacity: 0.45, pt: 0.3, px: 0.4 }}>
                                        No all-day bookings
                                    </Typography>
                                )}
                            </Stack>
                        ))}
                    </>
                ) : null}

                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: `${bodyRow.toString()} / ${(bodyRow + 1).toString()}`,
                        position: 'relative',
                    }}
                >
                    {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, index) => HOURS_START + index).map(
                        (hour) => {
                            const topPercentage = getHourLinePercentage(hour);
                            const isFirstHour = hour === HOURS_START;
                            const isLastHour = hour === HOURS_END;

                            return (
                                <Typography
                                    key={`hour-${hour.toString()}`}
                                    sx={{
                                        bottom: isLastHour ? 0 : 'auto',
                                        fontSize: '11px',
                                        letterSpacing: '0.04em',
                                        lineHeight: 1,
                                        opacity: 0.8,
                                        position: 'absolute',
                                        right: 8,
                                        top: isLastHour ? 'auto' : `${topPercentage.toString()}%`,
                                        transform: isFirstHour || isLastHour ? 'none' : 'translateY(-50%)',
                                        zIndex: 2,
                                    }}
                                >
                                    {getVisibleTimeLabel(hour)}
                                </Typography>
                            );
                        },
                    )}
                </Box>

                {visibleDays.map((day, index) => {
                    const nowLineVisible = day.isSame(currentTime, 'day') && currentTime.hour() >= HOURS_START;
                    const currentMinutes = currentTime.diff(
                        day.hour(HOURS_START).minute(0).second(0).millisecond(0),
                        'minute',
                    );
                    const currentLineTop = clampMinutes(currentMinutes);

                    return (
                        <Box
                            key={day.format('YYYY-MM-DD')}
                            sx={{
                                backgroundColor: day.isSame(currentTime, 'day')
                                    ? 'rgba(59,130,246,0.10)'
                                    : 'rgba(255,255,255,0.015)',
                                backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent calc((100% / ${(HOURS_END - HOURS_START).toString()}) - 1px), rgba(255,255,255,0.08) calc((100% / ${(HOURS_END - HOURS_START).toString()}) - 1px), rgba(255,255,255,0.08) calc(100% / ${(HOURS_END - HOURS_START).toString()}))`,
                                borderRight:
                                    index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                                gridRow: `${bodyRow.toString()} / ${(bodyRow + 1).toString()}`,
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    bottom: 0,
                                    left: 0,
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                }}
                            >
                                {/* Render half-hour/hour lines behind events to ensure visibility */}
                                <Box
                                    sx={{
                                        bottom: 0,
                                        left: 0,
                                        pointerEvents: 'none',
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        zIndex: 0,
                                    }}
                                >
                                    {Array.from({ length: (HOURS_END - HOURS_START) * 2 + 1 }, (_, idx) => {
                                        const top = (idx * (100 / ((HOURS_END - HOURS_START) * 2))).toString();
                                        const isHour = idx % 2 === 0;
                                        return (
                                            <Box
                                                key={`day-line-${day.format('YYYY-MM-DD')}-${idx.toString()}`}
                                                sx={{
                                                    borderTop: isHour
                                                        ? '1px solid rgba(255,255,255,0.14)'
                                                        : '1px solid rgba(255,255,255,0.05)',
                                                    left: 0,
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: `${top}%`,
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                                {dayData[index]?.positionedEvents.map((event) => {
                                    const top = (event.startMinutes * 100) / TOTAL_VISIBLE_MINUTES;
                                    const height = getEventHeightPercentage(event);
                                    const width = `calc(${(100 / event.laneCount).toString()}% - 6px)`;
                                    const left = `calc(${((100 / event.laneCount) * event.lane).toString()}% + 3px)`;
                                    const timedEventLayout = getTimedEventLayout({
                                        eventHeightPx: (height / 100) * gridBodyHeightPx,
                                        eventWidthPx: getEventWidthPx(event, dayColumnWidthPx),
                                        label: event.publicLabel,
                                        typography: eventTypography,
                                    });
                                    const eventStart = day
                                        .hour(HOURS_START)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0)
                                        .add(event.startMinutes, 'minute');
                                    const eventEnd = day
                                        .hour(HOURS_START)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0)
                                        .add(event.endMinutes, 'minute');
                                    const timeLabel = formatCompactEventTimeRange(eventStart, eventEnd);

                                    return (
                                        <Box
                                            key={event.id}
                                            sx={{
                                                backgroundColor: roomEventColor,
                                                border: '1px solid rgba(0,0,0,0.18)',
                                                borderRadius: 1,
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.18)',
                                                height: `calc(${height.toString()}% - ${(timedEventLayout.verticalInsetPx * 2).toString()}px)`,
                                                left,
                                                // Grow to exactly what the text paints, never to an
                                                // estimated worst case, so a short booking stays as
                                                // close to its own slot height as its content allows.
                                                minHeight: 'fit-content',
                                                overflow: 'hidden',
                                                position: 'absolute',
                                                top: `calc(${top.toString()}% + ${timedEventLayout.verticalInsetPx.toString()}px)`,
                                                width,
                                                zIndex: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    boxSizing: 'border-box',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: `${timedEventLayout.gapPx.toString()}px`,
                                                    minWidth: 0,
                                                    px: 0.25,
                                                    py: 0.25,
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: '-webkit-box',
                                                        fontSize: `${timedEventLayout.labelFontSizePx.toString()}px !important`,
                                                        fontWeight: 700,
                                                        lineHeight: EVENT_LINE_HEIGHT,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        overflowWrap: 'anywhere',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: timedEventLayout.labelLineClamp,
                                                    }}
                                                >
                                                    {event.publicLabel}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        display: '-webkit-box',
                                                        flexShrink: 0,
                                                        fontSize: `${timedEventLayout.timeFontSizePx.toString()}px !important`,
                                                        fontWeight: 600,
                                                        lineHeight: EVENT_LINE_HEIGHT,
                                                        opacity: 0.9,
                                                        // Narrow lanes break the range after the hyphen instead of
                                                        // ellipsing the end time away.
                                                        overflow: 'hidden',
                                                        overflowWrap: 'anywhere',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: timedEventLayout.timeLineCount,
                                                    }}
                                                >
                                                    {timeLabel}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {nowLineVisible && currentLineTop <= TOTAL_VISIBLE_MINUTES ? (
                                    <Box
                                        sx={{
                                            borderTop: '1px solid #ff6b6b',
                                            boxShadow: '0 0 3px rgba(0, 0, 0, 0.7)',
                                            left: 0,
                                            opacity: 0.9,
                                            position: 'absolute',
                                            right: 0,
                                            top: `${((currentLineTop * 100) / TOTAL_VISIBLE_MINUTES).toString()}%`,
                                            zIndex: 4,
                                        }}
                                    />
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {!hasTimedEvents && !hasAllDayEvents ? (
                <Typography sx={{ fontSize: 'clamp(12px, 1vw, 16px)', opacity: 0.7, textAlign: 'center' }}>
                    No room reservations were found for this week.
                </Typography>
            ) : null}
        </Stack>
    );
}

export default function InfoscreenRoomCalendarView({
    infoscreenLayout,
    roomCalendarConfig,
}: Readonly<InfoscreenRoomCalendarViewProps>) {
    const { instanceInfo } = useGetInstanceInfo();
    const timezone = infoscreenLayout?.tz || dayjs.tz.guess();
    const showWeekend = roomCalendarConfig?.showWeekend ?? true;
    const showEventTitles = roomCalendarConfig?.showEventTitles ?? true;
    const [currentTime, setCurrentTime] = useState(() => dayjs().tz(timezone));
    const [prevTimezone, setPrevTimezone] = useState(timezone);

    if (timezone !== prevTimezone) {
        setPrevTimezone(timezone);
        setCurrentTime(dayjs().tz(timezone));
    }

    const defaultViewMode = roomCalendarConfig?.defaultView === 'day' ? 'day' : 'week';
    const [viewMode, setViewMode] = useState<'day' | 'week'>(defaultViewMode);
    const [prevDefaultViewMode, setPrevDefaultViewMode] = useState(defaultViewMode);

    if (defaultViewMode !== prevDefaultViewMode) {
        setPrevDefaultViewMode(defaultViewMode);
        setViewMode(defaultViewMode);
    }

    const { data: ownedAssetsData } = useListAllOwnedIoTAssets(
        { instance: instanceInfo.instance_role },
        {
            swr: {
                enabled: Boolean(instanceInfo.instance_role),
                revalidateOnFocus: false,
            },
        },
    );

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setCurrentTime(dayjs().tz(timezone));
        }, 60_000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [timezone]);

    const roomGroupIdsKey = getConfiguredRoomGroupIds(roomCalendarConfig).join(',');
    const roomGroupIds = useMemo(() => (roomGroupIdsKey ? roomGroupIdsKey.split(',') : []), [roomGroupIdsKey]);
    const roomCalendarTargets = useMemo(
        () => roomGroupIds.map((groupId) => ({ ownerId: groupId, ownerType: 'socialgroup' as const })),
        [roomGroupIds],
    );

    const {
        data: calendars,
        hasAttemptedOwnerCalendar,
        hasLoadedCalendars,
        isEnsuringOwnerCalendar,
    } = useEnsureOwnerCalendars({
        revalidateOnFocus: false,
        targets: roomCalendarTargets,
    });

    const roomAssets = ownedAssetsData?.assets ?? [];
    const rooms: MultiRoomTarget[] = roomGroupIds.flatMap((groupId) => {
        const calendar = (calendars ?? []).find(
            (ownerCalendar) =>
                ownerCalendar.owner_type === 'socialgroup' && String(ownerCalendar.owner_id) === groupId,
        );

        if (!calendar) {
            return [];
        }

        const roomAsset = roomAssets.find(
            (asset) => (asset.asset_type ?? '').toLowerCase() === 'room' && String(asset.group_id ?? '') === groupId,
        );

        return [
            {
                calendarId: String(calendar.id),
                id: groupId,
                label: roomAsset?.name || calendar.name || 'Room',
            },
        ];
    });

    const isRoomCalendarLoading =
        !hasLoadedCalendars ||
        roomGroupIds.some(
            (groupId) =>
                isEnsuringOwnerCalendar('socialgroup', groupId) ||
                (!rooms.some((room) => room.id === groupId) && !hasAttemptedOwnerCalendar('socialgroup', groupId)),
        );

    if (roomGroupIds.length === 0) {
        return (
            <InfoscreenMessage
                infoscreenLayout={infoscreenLayout}
                secondaryText="Choose one or more room-linked social groups in the infoscreen tool to show reservations here."
                text="No room calendar selected"
            />
        );
    }

    if (isRoomCalendarLoading) {
        return <InfoscreenMessage infoscreenLayout={infoscreenLayout} text="Loading room calendar" />;
    }

    if (rooms.length === 0) {
        return (
            <InfoscreenMessage
                infoscreenLayout={infoscreenLayout}
                secondaryText="The selected social groups do not have an accessible calendar for this display context."
                text="Room calendar not available"
            />
        );
    }

    const toggleViewMode = () => {
        setViewMode((currentMode) => (currentMode === 'week' ? 'day' : 'week'));
    };

    return (
        <Box sx={{ height: '100%', position: 'relative', width: '100%' }}>
            {viewMode === 'day' ? (
                <InfoscreenMultiRoomCalendarView
                    currentTime={currentTime}
                    infoscreenLayout={infoscreenLayout}
                    rooms={rooms}
                    showEventTitles={showEventTitles}
                    timezone={timezone}
                />
            ) : rooms.length > 1 ? (
                <InfoscreenMultiRoomWeekCalendarView
                    currentTime={currentTime}
                    infoscreenLayout={infoscreenLayout}
                    rooms={rooms}
                    showEventTitles={showEventTitles}
                    showWeekend={showWeekend}
                    timezone={timezone}
                />
            ) : (
                <RoomWeekCalendar
                    calendarId={rooms[0]!.calendarId}
                    currentTime={currentTime}
                    infoscreenLayout={infoscreenLayout}
                    roomTitle={rooms[0]!.label}
                    showEventTitles={showEventTitles}
                    showWeekend={showWeekend}
                    timezone={timezone}
                />
            )}
            <Button
                onClick={toggleViewMode}
                size="small"
                sx={{
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 999,
                    color: infoscreenLayout?.textColor || '#ffffff',
                    fontSize: 'clamp(10px, 0.85vw, 13px)',
                    minWidth: 0,
                    position: 'absolute',
                    px: 1.5,
                    py: 0.4,
                    right: 8,
                    textTransform: 'none',
                    top: 8,
                    zIndex: 5,
                }}
            >
                {viewMode === 'week' ? 'Show day' : 'Show week'}
            </Button>
        </Box>
    );
}
